import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { assignableWorkspaceRoleSchema } from "@common/userWorkspaceRoles";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { WorkspaceDto } from "@common/dto/workspace/workspace.dto";
import { OrganizationServicesOffered } from "@common/types/organization";
import { createAccountingMigrationBodySchema } from "@common/schemas/accounting-integration.schema";
import { coaSetupBodySchema } from "@common/schemas/coa-setup.schema";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import { organizationDemoWorkspaceResponseSchema } from "@common/schemas/organization-demo-workspace.schema";
import {
    listOrganizationMemberClientAccessQuerySchema,
    listOrganizationMemberClientAccessResponseSchema,
    recordOrganizationMemberClientAccessBodySchema,
} from "@common/schemas/organization-member-client-access.schema";
import {
    EntityProfileType,
    EntityProfileIndustry,
    EntityCloseStatus,
    EntityCloseStage,
} from "../types/entity";
import { OrganizationEntityPriority } from "../types/organizationEntity";
import { WorkspaceTimezone } from "../workspaceTimezone";

const UpdateClientEntitySchema = z.object({
    bookkeepingStartDate: z.string().optional(),
    closeStatus: z.nativeEnum(EntityCloseStatus).optional(),
    closeDate: z.string().nullable().optional(),
    services: z.array(z.nativeEnum(OrganizationServicesOffered)).optional(),
    note: z.string().nullable().optional(),
    priority: z.nativeEnum(OrganizationEntityPriority).nullable().optional(),
    dueDate: z.string().nullable().optional(),
    closeStage: z.nativeEnum(EntityCloseStage).nullable().optional(),
});

const ClientEntitySchema = z.object({
    id: z.number(),
    name: z.string().nullable(),
    bookkeepingStartDate: z.string(),
    closeStatus: z.nativeEnum(EntityCloseStatus).nullable(),
    closeDate: z.string().nullable(),
    services: z.array(z.nativeEnum(OrganizationServicesOffered)).nullable(),
    priority: z.nativeEnum(OrganizationEntityPriority).nullable(),
    dueDate: z.string().nullable(),
});

export type ClientEntityResponseInterface = z.infer<typeof ClientEntitySchema>;

export const createOrganizationClientBodySchema = z.object({
    workspaceName: z.string(),
    entity: z.object({
        name: z.string(),
        legalType: z.nativeEnum(EntityProfileType),
        industries: z.array(z.nativeEnum(EntityProfileIndustry)).optional(),
        industryOther: z.string().optional(),
    }),
    bookkeepingStartDate: z.string().date(),
    timezone: z.nativeEnum(WorkspaceTimezone).optional(),
    servicesOffered: z.array(z.nativeEnum(OrganizationServicesOffered)),
    plan: z.nativeEnum(FLAT_RATE_PLANS),
    inviteClient: z
        .object({
            emails: z.array(z.string().email()),
            role: assignableWorkspaceRoleSchema,
        })
        .optional(),
    billingOwnership: z.boolean(),
    billingTransfer: z
        .object({
            email: z.string().email(),
        })
        .optional(),
    migration: createAccountingMigrationBodySchema.optional(),
    coaSetup: coaSetupBodySchema.optional(),
    isAccrualLedgerEnabled: z.boolean().optional(),
});

const createFirmWorkspaceBodySchema = z.object({
    name: z.string(),
    legalType: z.nativeEnum(EntityProfileType),
    bookkeepingStartDate: z.string().date(),
    timezone: z.nativeEnum(WorkspaceTimezone).optional(),
});

const c = initContract();

export const organizationClientContract = c.router(
    {
        createClient: {
            method: "POST",
            path: "/",
            body: createOrganizationClientBodySchema,
            responses: {
                201: z.object({ clientId: z.string().uuid() }),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        createFirmWorkspace: {
            method: "POST",
            path: "/firm-workspace",
            pathParams: z.object({
                organizationId: z.string().uuid(),
            }),
            body: createFirmWorkspaceBodySchema,
            responses: {
                200: c.noBody(),
                201: c.noBody(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        /**
         * @deprecated Use `createFirmWorkspace`. Kept for browser bundles
         * loaded before the rename; remove after the deploy window.
         */
        createFirmWorkspaceLegacy: {
            method: "POST",
            path: "/demo-client",
            pathParams: z.object({
                organizationId: z.string().uuid(),
            }),
            body: createFirmWorkspaceBodySchema,
            responses: {
                200: c.noBody(),
                201: c.noBody(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getDemoWorkspace: {
            method: "GET",
            path: "/demo-workspace",
            pathParams: z.object({
                organizationId: z.string().uuid(),
            }),
            responses: {
                200: organizationDemoWorkspaceResponseSchema,
                404: ErrorMessageSchema,
            },
        },
        updateClientEntity: {
            method: "PATCH",
            path: "/:clientId/entity/:entityId",
            pathParams: z.object({
                clientId: z.string().uuid(),
                entityId: z.coerce.number(),
            }),
            body: UpdateClientEntitySchema,
            responses: {
                200: ClientEntitySchema,
            },
        },
        updateClient: {
            method: "PATCH",
            path: "/:clientId",
            pathParams: z.object({
                clientId: z.string().uuid(),
            }),
            body: z.object({
                clientName: z.string(),
                plan: z.nativeEnum(FLAT_RATE_PLANS).optional(),
                bookkeepingStartDate: z.string(),
                servicesOffered: z
                    .array(z.nativeEnum(OrganizationServicesOffered))
                    .optional(),
                note: z.string().nullable().optional(),
            }),
            responses: {
                200: c.type<WorkspaceDto>(),
            },
        },
        patchClient: {
            method: "PATCH",
            path: "/:clientId/patch",
            pathParams: z.object({
                clientId: z.string().uuid(),
            }),
            body: z.object({
                note: z.string().nullable().optional(),
                servicesOffered: z
                    .array(z.nativeEnum(OrganizationServicesOffered))
                    .optional(),
                assigneeId: z.number().nullable().optional(),
            }),
            responses: {
                200: c.noBody(),
            },
        },
        deleteClient: {
            method: "DELETE",
            path: "/:clientId",
            pathParams: z.object({
                clientId: z.string().uuid(),
            }),
            responses: {
                200: c.noBody(),
            },
        },
        removeClient: {
            method: "POST",
            path: "/remove",
            body: z.object({
                clientId: z.string().uuid(),
            }),
            responses: {
                200: c.noBody(),
            },
        },
        transferBillingAndRemoveClient: {
            method: "POST",
            path: "/transfer-billing-and-remove",
            body: z.object({
                clientId: z.string().uuid(),
                newPrimaryAdminId: z.number(),
            }),
            responses: {
                200: c.noBody(),
            },
        },
        removeEntity: {
            method: "DELETE",
            path: "/:clientId/entity/:entityId",
            pathParams: z.object({
                clientId: z.string().uuid(),
                entityId: z.coerce.number(),
            }),
            responses: {
                200: c.noBody(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
                500: ErrorMessageSchema,
            },
        },
        recordClientAccess: {
            method: "POST",
            path: "/access",
            pathParams: z.object({
                organizationId: z.string().uuid(),
            }),
            body: recordOrganizationMemberClientAccessBodySchema,
            responses: {
                200: c.noBody(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        listClientAccess: {
            method: "GET",
            path: "/access",
            pathParams: z.object({
                organizationId: z.string().uuid(),
            }),
            query: listOrganizationMemberClientAccessQuerySchema,
            responses: {
                200: listOrganizationMemberClientAccessResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/organization-client/:organizationId",
    },
);
